import { memo, useMemo } from "react";
import { Dropdown, Flex, Select } from "@repo/ping-design";
import {
  LAYER_GOOGLE,
  MIN_ZOOM_REQUIRED_FOR_EAGLEVIEW,
  SpecialComponentProps,
  useIsMobile,
} from "@repo/ping-react-js";
import { DropdownSelectionType } from "@repo/ping-design";
import "./EagleViewImagerySelector.scss";

const getDisplayDate = (dateString: string): string => {
  const [year, month, day] = dateString.split("-");
  return `${month}/${day}/${year}`;
};

const formatLayerLabel = (layer: EagleviewLayer) => {
  if (layer.captureDate) {
    return getDisplayDate(layer.captureDate);
  }
  return layer.title;
};

export type EagleviewLayer = {
  id: string;
  title: string;
  style: string;
  tileMatrixSet: string;
  format: string;
  minZoom?: number;
  maxZoom?: number;
  captureDate?: string;
};

interface EagleViewImagerySelectorProps extends SpecialComponentProps {
  zoom?: number;
  center?: { lat: number; lng: number };
  onDateSelect?: (layer: any) => void;
  availableLayers?: Record<string, any>;
  enabledLayer?: { name: string; url?: string[] } | null;
  eagleviewLayers?: EagleviewLayer[];
  selectedEagleviewLayer?: EagleviewLayer | null;
  isLoadingEagleview?: boolean;
  eagleviewStatus?: "LOADING" | "READY" | "ERROR" | "NOT_AVAILABLE";
}

export const EagleViewImagerySelector = memo(
  ({
    onLayerSelect,
    zoom,
    center,
    onDateSelect,
    availableLayers,
    enabledLayer,
    eagleviewLayers = [],
    selectedEagleviewLayer,
    isLoadingEagleview = false,
    eagleviewStatus = "NOT_AVAILABLE",
  }: EagleViewImagerySelectorProps) => {
    const canViewEagleView = !!availableLayers?.EAGLEVIEW;
    const isMobile = useIsMobile();

    const dropdownOptions = useMemo(
      () =>
        eagleviewLayers.map((layer) => ({
          label: formatLayerLabel(layer),
          value: layer.id,
        })),
      [eagleviewLayers],
    );

    // Use the selected layer from Redux, which always reflects what's sent to the server
    const selectedLayerId = selectedEagleviewLayer?.id ?? null;

    const handleDateChange = (selection: any) => {
      if (selection.type === DropdownSelectionType.SimpleValue) {
        const layerId = selection.value;
        const layer = eagleviewLayers.find((l) => l.id === layerId);
        if (layer) {
          onDateSelect?.(layer);
        }
      }
    };

    const isZoomedInEnough =
      zoom !== undefined && zoom >= MIN_ZOOM_REQUIRED_FOR_EAGLEVIEW;

    const viewOptions = [{ label: "Vertical", value: "eagleview-vertical" }];

    const handleViewChange = (_: any, value: string) => {
      if (value === null) {
        onLayerSelect(LAYER_GOOGLE, {});
        return;
      }
      onLayerSelect({
        label: value.charAt(0).toUpperCase() + value.slice(1),
        name: value,
        url: [],
        layerType: "google",
        id: "EAGLEVIEW",
      });
    };

    const renderViewAndDateSelectors = () => (
      <Flex
        direction="column"
        gap="3"
        className="EagleViewImagerySelector__Dropdowns"
      >
        <Select
          options={viewOptions}
          value={enabledLayer?.name || ""}
          selectionType="single"
          onChange={handleViewChange}
          label="View"
          field="view"
          disabled={!isZoomedInEnough || eagleviewStatus === "ERROR"}
          customTemplateColumns={"1fr"}
        />
        <Dropdown
          options={dropdownOptions}
          selected={selectedLayerId}
          onChange={handleDateChange}
          placeholder="Select date"
          label="Select Date"
          iconName="Calendar"
          disabled={isLoadingEagleview || dropdownOptions.length === 0}
        />
      </Flex>
    );

    return (
      <div className="EagleViewImagerySelector">
        {!isMobile && (
          <h3 className="EagleViewImagerySelector__Title">EagleView Imagery</h3>
        )}

        {!canViewEagleView && (
          <p className="EagleViewImagerySelector__Error">
            EagleView imagery is not available for your account.
          </p>
        )}

        {canViewEagleView && !isZoomedInEnough && (
          <Flex direction="column" gap="1-5">
            {renderViewAndDateSelectors()}
            <p className="EagleViewImagerySelector__Error">
              Zoom in to enable EagleView imagery.
            </p>
          </Flex>
        )}

        {canViewEagleView &&
          isZoomedInEnough &&
          eagleviewStatus === "NOT_AVAILABLE" && (
            <Flex direction="column" gap="1-5">
              {renderViewAndDateSelectors()}
              <p className="EagleViewImagerySelector__Error">
                Select Vertical to enable EagleView imagery.
              </p>
            </Flex>
          )}

        {canViewEagleView &&
          isZoomedInEnough &&
          eagleviewStatus === "LOADING" && (
            <Flex direction="column" gap="1-5">
              {renderViewAndDateSelectors()}
              <p className="EagleViewImagerySelector__Error">
                Loading available dates...
              </p>
            </Flex>
          )}

        {canViewEagleView &&
          isZoomedInEnough &&
          eagleviewStatus === "ERROR" && (
            <Flex direction="column" gap="1-5">
              {renderViewAndDateSelectors()}
              <p className="EagleViewImagerySelector__Error">
                No EagleView imagery available for this location.
              </p>
            </Flex>
          )}

        {canViewEagleView &&
          isZoomedInEnough &&
          eagleviewStatus === "READY" &&
          eagleviewLayers.length > 0 &&
          renderViewAndDateSelectors()}
      </div>
    );
  },
);

EagleViewImagerySelector.displayName = "EagleViewImagerySelector";
